/**
 * 車両表示順 (truck display order) の比較規則を集約する utility。
 *
 * API (app/controllers/api/v3/plannings/trucks_controller.rb) と一致させる:
 *   1. displayOrder が設定されている車両 (= TruckDisplayOrderVersion 経由で固定された車両) を先に出す
 *   2. bucket 内では displayOrder ?? sortOrder ?? MAX 昇順
 *   3. 同値の場合は id 昇順 (安定化用)
 *
 * Frontend で displayOrder ?? sortOrder だけで比較していると、
 *   - 履歴に含まれない新規車両の sortOrder が小さい → 履歴あり車両より前に並ぶ
 * という API との乖離が出るため、必ずこの util を使うこと。
 */

// app/controllers/api/v3/plannings/trucks_controller.rb の `2**16` と揃える。
// MAX_SAFE_INTEGER を使うと sortOrder > 65536 の車両が来た場合に API と順序が逆転するため、
// この値を必ず使うこと。
const FALLBACK_ORDER = 2 ** 16;

type TruckOrderKeyInput = {
  id: number;
  displayOrder?: number | null;
  sortOrder?: number | null;
};

export const truckDisplayOrderSortKey = (
  truck: TruckOrderKeyInput,
): [number, number, number] => {
  const hasDisplayOrder = truck.displayOrder != null;
  const value = truck.displayOrder ?? truck.sortOrder ?? FALLBACK_ORDER;
  return [hasDisplayOrder ? 0 : 1, value, truck.id];
};

export const compareTrucksByDisplayOrder = (
  a: TruckOrderKeyInput,
  b: TruckOrderKeyInput,
): number => {
  const ka = truckDisplayOrderSortKey(a);
  const kb = truckDisplayOrderSortKey(b);
  for (let i = 0; i < ka.length; i += 1) {
    if (ka[i] !== kb[i]) return ka[i] - kb[i];
  }
  return 0;
};

/**
 * 保存済みの表示順 (storedIds) を、現在の車両一覧 (currentTruckIds) に同期させる。
 *
 * 車両の追加・削除が同時に起きて件数が変わらないケースでも正しく検知するため、
 * 件数比較ではなく id の集合差分で判定する:
 *   1. storedIds のうち現存する車両の id だけを順序を保って残す (= 削除分を除去)
 *   2. currentTruckIds のうち storedIds に無い車両の id を末尾に追加 (= 追加分)
 *
 * 追加も削除も無い場合は storedIds の参照をそのまま返す。これにより呼び出し側は
 * 参照比較 (`!==`) だけで変更を検知でき、不要な再 setState / 無限ループを防げる。
 */
export const reconcileSortedTruckIds = (
  storedIds: number[],
  currentTruckIds: number[],
): number[] => {
  const currentSet = new Set(currentTruckIds);
  const storedSet = new Set(storedIds);
  const activeIds = storedIds.filter((id) => currentSet.has(id));
  const newIds = currentTruckIds.filter((id) => !storedSet.has(id));

  // 削除なし (全 stored id が現存) かつ 追加なし → 内容・順序とも storedIds と同一。
  if (activeIds.length === storedIds.length && newIds.length === 0) {
    return storedIds;
  }
  return [...activeIds, ...newIds];
};
